"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { useSongs } from "@/lib/use-songs";

const STORAGE_KEY = "portfolio-music-player";
const POPUP_POSITION_KEY = "portfolio-music-popup-position";
const POPUP_VISIBLE_KEY = "portfolio-music-popup-visible";

export type RepeatMode = "off" | "one" | "all";

type PersistedState = {
  volume: number;
  lastSongId: string | null;
  repeatMode: RepeatMode;
  shuffle: boolean;
};

function loadPersistedState(): PersistedState {
  if (typeof window === "undefined") {
    return { volume: 0.8, lastSongId: null, repeatMode: "off", shuffle: false };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { volume: 0.8, lastSongId: null, repeatMode: "off", shuffle: false };
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    const validRepeat = ["off", "one", "all"].includes(parsed.repeatMode ?? "") ? parsed.repeatMode! : "off";
    return {
      volume: typeof parsed.volume === "number" && parsed.volume >= 0 && parsed.volume <= 1 ? parsed.volume : 0.8,
      lastSongId: typeof parsed.lastSongId === "string" ? parsed.lastSongId : null,
      repeatMode: validRepeat,
      shuffle: Boolean(parsed.shuffle),
    };
  } catch {
    return { volume: 0.8, lastSongId: null, repeatMode: "off", shuffle: false };
  }
}

function savePersistedState(state: PersistedState) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export function loadPopupPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 24, y: 24 };
  try {
    const raw = localStorage.getItem(POPUP_POSITION_KEY);
    if (!raw) return { x: 24, y: 24 };
    const parsed = JSON.parse(raw) as { x?: number; y?: number };
    return { x: parsed.x ?? 24, y: parsed.y ?? 24 };
  } catch {
    return { x: 24, y: 24 };
  }
}

export function savePopupPosition(x: number, y: number) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(POPUP_POSITION_KEY, JSON.stringify({ x, y }));
  } catch {}
}

export function loadPopupVisible(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = localStorage.getItem(POPUP_VISIBLE_KEY);
    return raw !== "false";
  } catch {
    return true;
  }
}

export function savePopupVisible(visible: boolean) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(POPUP_VISIBLE_KEY, String(visible));
  } catch {}
}

type MusicPlayerContextValue = {
  songs: ReturnType<typeof useSongs>["data"];
  loading: boolean;
  currentIndex: number;
  currentSong: ReturnType<typeof useSongs>["data"][number] | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  repeatMode: RepeatMode;
  shuffle: boolean;
  setVolume: (v: number) => void;
  setRepeatMode: (m: RepeatMode) => void;
  setShuffle: (s: boolean) => void;
  togglePlay: () => void;
  goNext: () => void;
  goPrev: () => void;
  goToIndex: (index: number, autoPlay?: boolean) => void;
  selectSong: (index: number) => void;
  seekToPercent: (percent: number) => void;
  seekFromEvent: (e: React.MouseEvent<HTMLDivElement>) => void;
  discImageSrc: string;
  discImageAlt: string;
};

const MusicPlayerContext = createContext<MusicPlayerContextValue | null>(null);

export function useMusicPlayer() {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) throw new Error("useMusicPlayer must be used within MusicPlayerProvider");
  return ctx;
}

export function useMusicPlayerOptional() {
  return useContext(MusicPlayerContext);
}

function cycleRepeat(mode: RepeatMode): RepeatMode {
  if (mode === "off") return "one";
  if (mode === "one") return "all";
  return "off";
}

export function MusicPlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { data: songs, loading } = useSongs();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => loadPersistedState().volume);
  const [repeatMode, setRepeatMode] = useState<RepeatMode>(() => loadPersistedState().repeatMode);
  const [shuffle, setShuffle] = useState(() => loadPersistedState().shuffle);

  const currentSong = songs[currentIndex] ?? null;

  const play = useCallback(() => {
    if (!audioRef.current || !currentSong) return;
    audioRef.current.play().catch(() => {});
    setIsPlaying(true);
  }, [currentSong]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (!currentSong) return;
    if (isPlaying) pause();
    else play();
  }, [currentSong, isPlaying, play, pause]);

  const goToIndex = useCallback(
    (index: number, autoPlay = true) => {
      if (songs.length === 0 || index < 0 || index >= songs.length) return;
      setCurrentIndex(index);
      setCurrentTime(0);
      setIsPlaying(autoPlay);
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.src = songs[index].audio_url;
        if (autoPlay) audioRef.current.play().catch(() => {});
      }
    },
    [songs]
  );

  const handleSongEnd = useCallback(() => {
    if (songs.length === 0 || !audioRef.current) return;
    if (repeatMode === "one") {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
      return;
    }
    if (shuffle) {
      if (songs.length === 1) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
        return;
      }
      let nextIdx = Math.floor(Math.random() * songs.length);
      while (nextIdx === currentIndex && songs.length > 1) nextIdx = Math.floor(Math.random() * songs.length);
      goToIndex(nextIdx, true);
      return;
    }
    if (currentIndex === songs.length - 1) {
      if (repeatMode === "all") goToIndex(0, true);
      else setIsPlaying(false);
    } else {
      goToIndex(currentIndex + 1, true);
    }
  }, [songs, currentIndex, repeatMode, shuffle, goToIndex]);

  const goNext = useCallback(() => {
    if (songs.length === 0) return;
    if (shuffle) {
      if (songs.length === 1) {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {});
        }
        return;
      }
      let nextIdx = Math.floor(Math.random() * songs.length);
      while (nextIdx === currentIndex) nextIdx = Math.floor(Math.random() * songs.length);
      goToIndex(nextIdx, true);
    } else {
      goToIndex((currentIndex + 1) % songs.length, true);
    }
  }, [songs, currentIndex, shuffle, goToIndex]);

  const goPrev = useCallback(() => {
    if (songs.length === 0) return;
    if (currentTime > 3) {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        setCurrentTime(0);
      }
      return;
    }
    const prev = currentIndex === 0 ? songs.length - 1 : currentIndex - 1;
    setCurrentIndex(prev);
    setCurrentTime(0);
    setIsPlaying(true);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.src = songs[prev].audio_url;
      audioRef.current.play().catch(() => {});
    }
  }, [songs, currentIndex, currentTime]);

  const seekToPercent = useCallback(
    (percent: number) => {
      if (!audioRef.current || !currentSong || !duration) return;
      const clamped = Math.max(0, Math.min(1, percent));
      const time = clamped * duration;
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    },
    [duration, currentSong]
  );

  const seekFromEvent = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (!duration || !el) return;
      const rect = el.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      seekToPercent(percent);
    },
    [duration, seekToPercent]
  );

  const selectSong = useCallback(
    (index: number) => {
      if (index === currentIndex && isPlaying) return;
      setCurrentIndex(index);
      setCurrentTime(0);
      setIsPlaying(true);
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.src = songs[index].audio_url;
        audioRef.current.play().catch(() => {});
      }
    },
    [songs, currentIndex, isPlaying]
  );

  useEffect(() => {
    if (!currentSong || !audioRef.current) return;
    audioRef.current.src = currentSong.audio_url;
    if (isPlaying) audioRef.current.play().catch(() => {});
  }, [currentSong, isPlaying]);

  useEffect(() => {
    if (!audioRef.current || !currentSong) return;
    if (isPlaying) audioRef.current.play().catch(() => {});
    else audioRef.current.pause();
  }, [isPlaying, currentSong]);

  // Đảm bảo nhạc tiếp tục phát khi chuyển trang — gọi lại play() mỗi khi pathname đổi
  const pathname = usePathname();
  const prevPathname = useRef<string | null>(null);
  useEffect(() => {
    if (prevPathname.current !== null && pathname !== prevPathname.current) {
      if (isPlaying && audioRef.current && currentSong) {
        audioRef.current.play().catch(() => {});
      }
    }
    prevPathname.current = pathname;
  }, [pathname, isPlaying, currentSong]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => setDuration(audio.duration);
    const onEnded = () => handleSongEnd();
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("ended", onEnded);
    };
  }, [handleSongEnd]);

  useEffect(() => {
    if (songs.length === 0) return;
    const { lastSongId } = loadPersistedState();
    if (lastSongId) {
      const idx = songs.findIndex((s) => s.id === lastSongId);
      if (idx >= 0) setCurrentIndex(idx);
    }
  }, [songs]);

  useEffect(() => {
    if (!currentSong) return;
    savePersistedState({ volume, lastSongId: currentSong.id, repeatMode, shuffle });
  }, [volume, currentSong, repeatMode, shuffle]);

  const showCover = isPlaying && currentSong?.cover_url;
  const discImageSrc = showCover ? currentSong!.cover_url! : "";
  const discImageAlt = showCover ? currentSong!.title : "";

  const value: MusicPlayerContextValue = {
    songs,
    loading,
    currentIndex,
    currentSong,
    isPlaying,
    currentTime,
    duration,
    volume,
    repeatMode,
    shuffle,
    setVolume,
    setRepeatMode: (m) => setRepeatMode(m),
    setShuffle,
    togglePlay,
    goNext,
    goPrev,
    goToIndex,
    selectSong,
    seekToPercent,
    seekFromEvent,
    discImageSrc,
    discImageAlt,
  };

  return (
    <MusicPlayerContext.Provider value={value}>
      <audio ref={audioRef} preload="metadata" className="sr-only" aria-hidden playsInline />
      {children}
    </MusicPlayerContext.Provider>
  );
}
